// Lo PURO de renovar firmas de Storage: reconocer una URL firmada de Supabase, leer
// cuándo vence y reemplazar URLs adentro de los mensajes del chat
// (ADR-2026-09-12-las-fotos-viajan-con-firma-fresca). Sin red ni cliente de Supabase
// para poder testearlo; la firma en sí vive en `firmasFrescas.ts`.
//
// Una URL firmada tiene la forma `<host>/storage/v1/object/sign/<bucket>/<path>?token=<jwt>`
// y el token trae `exp` (segundos Unix). El token se LEE, no se verifica: sólo sirve para
// decidir si conviene renovar antes de mandarla.

/** Los parámetros de una URL de `render/image` (la transformación de Storage). */
export interface TransformDeStorage {
  width?: number;
  height?: number;
  resize?: 'cover' | 'contain' | 'fill';
  quality?: number;
}

export interface FirmaLeida {
  bucket: string;
  path: string;
  /** Vencimiento en segundos Unix; `null` si el token no se pudo leer. */
  exp: number | null;
  /** Sólo en una URL de `render/image/sign`: cómo la reescala Storage. Renovarla exige
   *  volver a firmar CON la misma transformación, si no vuelve el original. */
  transform?: TransformDeStorage;
}

/**
 * La miniatura que el chat manda sólo para IDENTIFICAR una creación ("las últimas
 * generaciones que te mostré", `show_recent_media`): 400 px de lado mayor, calidad 75.
 * El modelo no la analiza, la reconoce; a 800 px (el `opt/`) cada una costaba ~1.000
 * tokens de entrada en cada turno siguiente, y en Claude además se reescribe en caché
 * (ADR-2026-09-26-media-pasa-a-gpt-6-sol-y-baja-a-gemini-3-8).
 */
export const TRANSFORM_MINIATURA_PARA_IDENTIFICAR: Required<TransformDeStorage> = {
  width: 400,
  height: 400,
  resize: 'contain',
  quality: 75,
};

const RESIZES = new Set(['cover', 'contain', 'fill']);

function leerTransform(q: URLSearchParams): TransformDeStorage | undefined {
  const t: TransformDeStorage = {};
  const num = (k: string) => {
    const v = Number(q.get(k));
    return Number.isFinite(v) && v > 0 ? v : undefined;
  };
  const width = num('width');
  const height = num('height');
  const quality = num('quality');
  const resize = q.get('resize');
  if (width) t.width = width;
  if (height) t.height = height;
  if (quality) t.quality = quality;
  if (resize && RESIZES.has(resize)) t.resize = resize as TransformDeStorage['resize'];
  return Object.keys(t).length > 0 ? t : undefined;
}

/** Con menos de esto por delante, se renueva: el modelo o la generación pueden tardar. */
export const MARGEN_RENOVACION_S = 10 * 60;

const decodificarBase64Url = (s: string): string => {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const relleno = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  if (typeof atob === 'function') return atob(relleno);
  return Buffer.from(relleno, 'base64').toString('binary');
};

const expDelToken = (token: string): number | null => {
  try {
    const partes = token.split('.');
    if (partes.length < 2) return null;
    const payload = JSON.parse(decodificarBase64Url(partes[1])) as { exp?: unknown };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
};

/**
 * Parsea una URL firmada de Storage; `null` si no lo es (pública, data:, blob:, otro host).
 * Reconoce las dos formas firmadas: `object/sign` (el objeto tal cual) y
 * `render/image/sign` (reescalado por Storage; los parámetros quedan en `transform`).
 */
export function leerFirma(url: string): FirmaLeida | null {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  const m = u.pathname.match(/\/storage\/v1\/(object|render\/image)\/sign\/([^/]+)\/(.+)$/);
  if (!m) return null;
  const token = u.searchParams.get('token');
  if (!token) return null;
  let path: string;
  try {
    path = decodeURIComponent(m[3]);
  } catch {
    path = m[3];
  }
  const transform = m[1] === 'render/image' ? leerTransform(u.searchParams) : undefined;
  return { bucket: m[2], path, exp: expDelToken(token), ...(transform ? { transform } : {}) };
}

/** ¿Hay que renovarla? Si el token no se pudo leer, también: mejor una firma de más. */
export const firmaPorVencer = (firma: FirmaLeida, ahoraS = Date.now() / 1000): boolean =>
  firma.exp === null || firma.exp - ahoraS < MARGEN_RENOVACION_S;

/**
 * Las URLs firmadas por vencer, agrupadas por bucket (path → URL original). Se firman en
 * lote. Las transformadas (`render/image`) NO entran acá: van por `transformadasPorVencer`,
 * porque el lote de Storage no acepta transformación.
 */
export function agruparPorVencer(
  urls: string[],
  ahoraS = Date.now() / 1000,
): Map<string, Map<string, string>> {
  const porBucket = new Map<string, Map<string, string>>();
  for (const url of urls) {
    const firma = leerFirma(url);
    if (!firma || firma.transform || !firmaPorVencer(firma, ahoraS)) continue;
    let paths = porBucket.get(firma.bucket);
    if (!paths) {
      paths = new Map();
      porBucket.set(firma.bucket, paths);
    }
    if (!paths.has(firma.path)) paths.set(firma.path, url);
  }
  return porBucket;
}

/** Las URLs transformadas (`render/image/sign`) por vencer, una por una y sin repetir:
 *  cada una se vuelve a firmar con SU transformación. */
export function transformadasPorVencer(
  urls: string[],
  ahoraS = Date.now() / 1000,
): Array<{ url: string; bucket: string; path: string; transform: TransformDeStorage }> {
  const out: Array<{ url: string; bucket: string; path: string; transform: TransformDeStorage }> = [];
  const vistas = new Set<string>();
  for (const url of urls) {
    if (vistas.has(url)) continue;
    const firma = leerFirma(url);
    if (!firma?.transform || !firmaPorVencer(firma, ahoraS)) continue;
    vistas.add(url);
    out.push({ url, bucket: firma.bucket, path: firma.path, transform: firma.transform });
  }
  return out;
}

type Bloque = { type?: unknown; source?: { type?: unknown; url?: unknown }; content?: unknown };

/** Recorre los bloques de contenido de un mensaje (y los de adentro de un `tool_result`). */
function* bloquesDe(content: unknown): Generator<Bloque> {
  if (!Array.isArray(content)) return;
  for (const b of content) {
    if (!b || typeof b !== 'object') continue;
    const bloque = b as Bloque;
    yield bloque;
    if (bloque.type === 'tool_result') yield* bloquesDe(bloque.content);
  }
}

const esBloqueConUrl = (b: Bloque): b is Bloque & { source: { type: 'url'; url: string } } =>
  (b.type === 'image' || b.type === 'document') &&
  !!b.source &&
  b.source.type === 'url' &&
  typeof b.source.url === 'string';

/**
 * El path del `opt/` (thumbnail de 800px que escribe el backend) que acompaña a un
 * `full/`, en los buckets donde existe esa convención — o `null` si no hay uno posible.
 *
 * Es lo que el MODELO tiene que ver en vez del original
 * (ADR-2026-09-14-el-modelo-ve-el-opt-y-el-error-habla-en-castellano): Anthropic se baja
 * las imágenes por URL desde São Paulo y con ~2 MB ya se le agota el tiempo, y de todos
 * modos reescala lo que recibe. El `opt/` pesa ~70 KB donde el `full/` de un 4K pesa 8.
 * La GENERACIÓN sigue recibiendo el `full/`: viaja en `reference_images`, no en un
 * bloque `image`.
 *
 *   generadas: `<empresa>/full/<archivo>`            → `<empresa>/opt/<archivo>`
 *   productos: `<empresa>/<variante>/full/<archivo>` → `<empresa>/<variante>/opt/<archivo>`
 *
 * `modelos`, `fondos_personalizados`, `moodboards` y las subidas del chat no tienen
 * `opt/`: ahí lo que acota es el tope de subida (`aligerarImagen`).
 */
export function pathLiviano(bucket: string, path: string): string | null {
  const forma =
    bucket === 'generadas'
      ? /^([^/]+)\/full\/([^/]+)$/
      : bucket === 'productos'
        ? /^([^/]+\/[^/]+)\/full\/([^/]+)$/
        : null;
  if (!forma) return null;
  const m = path.match(forma);
  return m ? `${m[1]}/opt/${m[2]}` : null;
}

/**
 * Los bloques `image` por URL firmada que tienen un `opt/` posible, agrupados por bucket
 * como `path liviano → URL original` (la original es la clave para reemplazar después).
 * Los `document` no entran: un PDF no tiene thumbnail.
 */
export function agruparAligerables(
  mensajes: Array<{ content: unknown }>,
): Map<string, Map<string, string>> {
  const porBucket = new Map<string, Map<string, string>>();
  for (const m of mensajes) {
    for (const b of bloquesDe(m.content)) {
      if (!esBloqueConUrl(b) || b.type !== 'image') continue;
      const firma = leerFirma(b.source.url);
      if (!firma) continue;
      const liviano = pathLiviano(firma.bucket, firma.path);
      if (!liviano) continue;
      let paths = porBucket.get(firma.bucket);
      if (!paths) {
        paths = new Map();
        porBucket.set(firma.bucket, paths);
      }
      if (!paths.has(liviano)) paths.set(liviano, b.source.url);
    }
  }
  return porBucket;
}

/** Todas las URLs de bloques `image`/`document` por URL que hay en los mensajes. */
export function urlsEnMensajes(mensajes: Array<{ content: unknown }>): string[] {
  const out: string[] = [];
  for (const m of mensajes) for (const b of bloquesDe(m.content)) if (esBloqueConUrl(b)) out.push(b.source.url);
  return out;
}

/** Reemplaza EN EL LUGAR las URLs que estén en `renovadas` (vieja → nueva). Devuelve cuántas. */
export function reemplazarUrlsEnMensajes(
  mensajes: Array<{ content: unknown }>,
  renovadas: Map<string, string>,
): number {
  let n = 0;
  for (const m of mensajes) {
    for (const b of bloquesDe(m.content)) {
      if (!esBloqueConUrl(b)) continue;
      const nueva = renovadas.get(b.source.url);
      if (!nueva || nueva === b.source.url) continue;
      b.source = { ...b.source, url: nueva };
      n += 1;
    }
  }
  return n;
}
